import { Component, OnInit } from '@angular/core';
import { SegurosApiService } from '../../services/seguros-api.service';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Swal from 'sweetalert2';

/**
 * Activaciones pendientes post-pago (Plan 28, 2026-08-03).
 *
 * Vista operativa para casos donde el cliente pagó pero el hook a Venemergencias
 * falló (temporal o permanentemente). Alimentada por 3 endpoints admin:
 *   GET  /api/insurance/tudoc/failed-affiliations
 *   POST /api/insurance/tudoc/failed-affiliations/:policyCode/retry
 *   POST /api/insurance/tudoc/failed-affiliations/:policyCode/mark-resolved
 *
 * Filtros: rango de fechas + status (failed|escalated|resolved|all) + búsqueda
 * por CI/nombre/teléfono/email.
 *
 * Acciones por fila:
 *   - Reintentar afiliación: dispara el hook desde el backend con source=ADMIN
 *     (sin límite de intentos). Si el gateway responde OK, la Policy pasa a
 *     COMPLETED y el widget del home del cliente reaparece automáticamente.
 *   - Marcar como resuelto: valida contra el gateway VE (GET /ve/tudoc/{ci})
 *     como test de la verdad. Sólo procede si el aliado tiene el registro.
 *     Sin override manual — si el aliado no lo tiene, se aborta con mensaje.
 */

type StatusFilter = 'all' | 'failed' | 'escalated' | 'resolved';

interface FailedAffiliationRow {
  policy_code: string;
  policy_status: string;
  created_at: string;
  updated_at: string;
  insured: {
    ci: string | null;
    name: string | null;
    phone: string | null;
    email: string | null;
    birth_day: string | null;
    gender: string | null;
    place: string | null;
    person_code: string | null;
  };
  plan: {
    months: number | null;
    insurance_code: string | null;
    origin: string | null;
    plan_id: string | null;
  };
  payment: {
    amount_usd: number | null;
    amount_bs: number | null;
    method: string | null;
    reference: string | null;
    timestamp: string | null;
  };
  retries: { count: number; max: number; remaining: number };
  escalated: { flag: boolean; at: string | null; by: string | null; reason: string | null };
  resolved: { flag: boolean; at: string | null; by: string | null; comment: string | null };
  last_hook_error: { status: number | null; msg: string | null; at: string | null };
  integration_data: { sav_insured_id: string | null; start_date: string | null; end_date: string | null };
}

interface FailedAffiliationsKpis {
  total: number;
  escalated: number;
  resolved: number;
  awaiting_action: number;
  avg_retries: number;
}

@Component({
  selector: 'app-tudoc-failed-affiliations',
  templateUrl: './tudoc-failed-affiliations.component.html',
  styleUrls: ['./tudoc-failed-affiliations.component.css']
})
export class TudocFailedAffiliationsComponent implements OnInit {
  // Filtros
  startDate = '';
  endDate = '';
  statusFilter: StatusFilter = 'all';
  personSearch = '';

  // Data
  items: FailedAffiliationRow[] = [];
  kpis: FailedAffiliationsKpis | null = null;
  page = 1;
  pageSize = 20;
  total = 0;

  // UI state
  loading = false;
  loadError: string | null = null;

  // Row-level action state
  retryingCode: string | null = null;
  resolvingCode: string | null = null;
  expandedCode: string | null = null;
  actionMsg: { code: string; type: 'success' | 'error' | 'info'; text: string } | null = null;

  // Modal state
  resolveModalOpen = false;
  resolveTargetCode: string | null = null;
  resolveComment = '';
  resolveError: string | null = null;

  constructor(private api: SegurosApiService) {}

  ngOnInit(): void {
    // Default: últimos 30 días.
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    this.endDate = today.toISOString().slice(0, 10);
    this.startDate = thirtyDaysAgo.toISOString().slice(0, 10);
    this.load();
  }

  load(): void {
    this.loading = true;
    this.loadError = null;
    this.api.getTudocFailedAffiliations({
      startDate: this.startDate || undefined,
      endDate: this.endDate || undefined,
      status: this.statusFilter,
      personSearch: this.personSearch?.trim() || undefined,
      page: this.page,
      pageSize: this.pageSize
    }).subscribe({
      next: (resp) => {
        this.items = resp?.items || [];
        this.kpis = resp?.kpis || null;
        this.total = resp?.total || 0;
        this.loading = false;
      },
      error: (err) => {
        console.error('[FailedAffiliations] load error', err);
        this.loadError = err?.error?.message || err?.message || 'Error cargando activaciones pendientes';
        this.loading = false;
      }
    });
  }

  onApplyFilters(): void {
    this.page = 1;
    this.load();
  }

  onClearFilters(): void {
    this.startDate = '';
    this.endDate = '';
    this.statusFilter = 'all';
    this.personSearch = '';
    this.page = 1;
    this.load();
  }

  goToPage(p: number): void {
    if (p < 1 || (this.totalPages > 0 && p > this.totalPages)) return;
    this.page = p;
    this.load();
  }

  get totalPages(): number {
    return this.pageSize > 0 ? Math.ceil(this.total / this.pageSize) : 0;
  }

  toggleExpand(code: string): void {
    this.expandedCode = this.expandedCode === code ? null : code;
  }

  statusBadge(row: FailedAffiliationRow): { label: string; cls: string } {
    if (row.resolved.flag) return { label: 'Resuelta', cls: 'bg-green-100 text-green-800' };
    if (row.escalated.flag) return { label: 'Escalada', cls: 'bg-red-100 text-red-800' };
    return { label: 'Pendiente', cls: 'bg-yellow-100 text-yellow-800' };
  }

  // ── Acción: Reintentar (source=ADMIN) ───────────────────────────────
  async onRetry(row: FailedAffiliationRow): Promise<void> {
    if (this.retryingCode || this.resolvingCode) return;
    if (row.resolved.flag) return;

    const confirmed = await Swal.fire({
      icon: 'question',
      title: '¿Reintentar afiliación?',
      html: `Se reintentará la afiliación del asegurado <b>${row.insured.name || row.insured.ci}</b> en Venemergencias.<br><br>Como administrador, este intento <b>no consume</b> los intentos del cliente.`,
      showCancelButton: true,
      confirmButtonText: 'Sí, reintentar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#2563eb',
      cancelButtonColor: '#6b7280',
      reverseButtons: true,
      focusCancel: true
    });
    if (!confirmed.isConfirmed) return;

    this.retryingCode = row.policy_code;
    this.actionMsg = null;

    this.api.retryTudocAffiliationAdmin(row.policy_code).subscribe({
      next: (resp) => {
        this.retryingCode = null;
        if (resp?.ok && !resp?.notification_error) {
          this.actionMsg = {
            code: row.policy_code,
            type: 'success',
            text: 'Reintento exitoso. El cliente verá su afiliación activa en el próximo refresh.'
          };
        } else {
          // notification_error true — el hook falló otra vez, pero source=ADMIN no consume del contador cliente.
          const msg = resp?.error_body?.message || 'El aliado no respondió correctamente. Reintenta en unos minutos.';
          this.actionMsg = { code: row.policy_code, type: 'error', text: msg };
        }
        this.load();
      },
      error: (err) => {
        this.retryingCode = null;
        const code = err?.error?.code;
        const text =
          code === 'ALREADY_RESOLVED'
            ? 'Esta afiliación ya fue resuelta manualmente.'
            : (err?.error?.message || err?.message || 'Error inesperado');
        this.actionMsg = { code: row.policy_code, type: 'error', text };
      }
    });
  }

  // ── Acción: Marcar como resuelto (modal) ────────────────────────────
  openResolveModal(row: FailedAffiliationRow): void {
    if (row.resolved.flag) return;
    this.resolveTargetCode = row.policy_code;
    this.resolveComment = '';
    this.resolveError = null;
    this.resolveModalOpen = true;
  }

  closeResolveModal(): void {
    this.resolveModalOpen = false;
    this.resolveTargetCode = null;
    this.resolveComment = '';
    this.resolveError = null;
  }

  async confirmResolve(): Promise<void> {
    if (!this.resolveTargetCode) return;
    if (!this.resolveComment || this.resolveComment.trim().length < 5) {
      this.resolveError = 'El comentario es obligatorio (mínimo 5 caracteres).';
      return;
    }

    const confirmed = await Swal.fire({
      icon: 'warning',
      title: '¿Marcar como resuelto?',
      html: '¿Confirmas que Venemergencias resolvió este caso?<br><br>El sistema <b>consultará al aliado</b> para validar los datos antes de marcar como resuelto. Si el aliado no tiene el registro, la acción se cancela.',
      showCancelButton: true,
      confirmButtonText: 'Sí, validar y resolver',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#059669',
      cancelButtonColor: '#6b7280',
      reverseButtons: true,
      focusCancel: true
    });
    if (!confirmed.isConfirmed) return;

    this.resolvingCode = this.resolveTargetCode;
    this.resolveError = null;

    this.api.markTudocAffiliationResolved(this.resolveTargetCode, this.resolveComment.trim()).subscribe({
      next: (resp) => {
        const code = this.resolveTargetCode!;
        this.resolvingCode = null;
        this.closeResolveModal();
        this.actionMsg = {
          code,
          type: 'success',
          text: 'Resuelto. Datos obtenidos del aliado. El widget del cliente aparecerá activo en el próximo refresh.'
        };
        this.load();
      },
      error: (err) => {
        this.resolvingCode = null;
        const code = err?.error?.code;
        if (code === 'GATEWAY_HAS_NO_RECORD' || code === 'GATEWAY_INCOMPLETE_DATA') {
          this.resolveError = err?.error?.message || 'Venemergencias aún no ha registrado esta cédula. Coordina con el aliado antes de reintentar.';
        } else if (code === 'GATEWAY_UNAVAILABLE') {
          this.resolveError = err?.error?.message || 'El servicio de Venemergencias no responde. Usa "Reintentar afiliación" o vuelve a intentar en unos minutos.';
        } else if (code === 'ALREADY_RESOLVED') {
          this.resolveError = 'Esta afiliación ya fue resuelta.';
        } else {
          this.resolveError = err?.error?.message || err?.message || 'Error inesperado';
        }
      }
    });
  }

  // ── Exportar XLSX para enviar a Venemergencias ──────────────────────
  exporting = false;
  onExport(): void {
    if (this.exporting) return;
    this.exporting = true;
    // Fetch sin paginacion (pageSize alto). Backend cap = 100 pero el endpoint
    // acepta ambos casos; usamos el mismo listado con un size que cubre >99% de casos.
    this.api.getTudocFailedAffiliations({
      startDate: this.startDate || undefined,
      endDate: this.endDate || undefined,
      status: this.statusFilter,
      personSearch: this.personSearch?.trim() || undefined,
      page: 1,
      pageSize: 100
    }).subscribe({
      next: (resp) => {
        this.exporting = false;
        const items = resp?.items || [];
        if (!items.length) {
          Swal.fire({
            icon: 'info',
            title: 'Sin datos para exportar',
            text: 'No hay items que coincidan con los filtros actuales.'
          });
          return;
        }
        this.downloadXlsx(items);
      },
      error: (err) => {
        this.exporting = false;
        console.error('[FailedAffiliations] export fetch error', err);
        Swal.fire({
          icon: 'error',
          title: 'Error al exportar',
          text: 'No se pudo obtener los datos: ' + (err?.error?.message || err?.message || 'desconocido')
        });
      }
    });
  }

  private downloadXlsx(items: FailedAffiliationRow[]): void {
    const isoOrEmpty = (d: any) => d ? new Date(d).toISOString() : '';
    const state = (row: FailedAffiliationRow) =>
      row.resolved?.flag ? 'RESUELTA' : (row.escalated?.flag ? 'ESCALADA' : 'PENDIENTE');

    // Filas planas para Excel. Encabezados en espanol amigable para el equipo.
    const rows = items.map(it => ({
      'Fecha creacion':           isoOrEmpty(it.created_at),
      'Policy code':              it.policy_code,
      'Estado':                   state(it),
      'CI':                       it.insured?.ci || '',
      'Nombre':                   it.insured?.name || '',
      'Telefono':                 it.insured?.phone || '',
      'Email':                    it.insured?.email || '',
      'Fecha nacimiento':         it.insured?.birth_day || '',
      'Genero':                   it.insured?.gender || '',
      'Plaza':                    it.insured?.place || '',
      'Meses':                    it.plan?.months ?? '',
      'Insurance code':           it.plan?.insurance_code || '',
      'Origen':                   it.plan?.origin || '',
      'Plan ID':                  it.plan?.plan_id || '',
      'Monto USD':                it.payment?.amount_usd ?? '',
      'Monto Bs':                 it.payment?.amount_bs ?? '',
      'Metodo pago':              it.payment?.method || '',
      'Referencia':               it.payment?.reference || '',
      'Fecha pago':               isoOrEmpty(it.payment?.timestamp),
      'sav_insured_id':           it.integration_data?.sav_insured_id || '',
      'Start date (aliado)':      isoOrEmpty(it.integration_data?.start_date),
      'End date (aliado)':        isoOrEmpty(it.integration_data?.end_date),
      'Reintentos usados':        it.retries?.count ?? 0,
      'Reintentos max':           it.retries?.max ?? 2,
      'Escalada':                 it.escalated?.flag ? 'SI' : 'NO',
      'Escalada en':              isoOrEmpty(it.escalated?.at),
      'Escalada por':             it.escalated?.by || '',
      'Motivo escalada':          it.escalated?.reason || '',
      'Resuelta':                 it.resolved?.flag ? 'SI' : 'NO',
      'Resuelta en':              isoOrEmpty(it.resolved?.at),
      'Resuelta por':             it.resolved?.by || '',
      'Comentario resolucion':    it.resolved?.comment || '',
      'Ultimo error status':      it.last_hook_error?.status ?? '',
      'Ultimo error msg':         it.last_hook_error?.msg || '',
      'Ultimo error en':          isoOrEmpty(it.last_hook_error?.at)
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    // Anchos de columna aproximados para que el archivo se lea bien al abrir.
    ws['!cols'] = Object.keys(rows[0]).map(k => ({
      wch: Math.min(Math.max(k.length + 2, 12), 40)
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Activaciones pendientes');

    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const stamp = new Date().toISOString().slice(0, 10);
    saveAs(blob, `tudoc-activaciones-pendientes-${stamp}.xlsx`);
  }

  // ── Helpers de formato ──────────────────────────────────────────────
  formatDate(iso: string | null): string {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('es-VE', { dateStyle: 'short', timeStyle: 'short' }); } catch { return iso; }
  }

  formatDateShort(iso: string | null): string {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('es-VE'); } catch { return iso; }
  }

  formatAmount(usd: number | null, bs: number | null): string {
    const parts: string[] = [];
    if (usd != null) parts.push(`$${Number(usd).toFixed(2)}`);
    if (bs != null) parts.push(`Bs ${Number(bs).toFixed(2)}`);
    return parts.join(' · ') || '—';
  }
}
